import { screenToWorld, type Camera, type Point, type Rect } from '../camera';
import { edgeAt, handleAt, handlesFor, toLocal, toWorld, type HandleId } from '../handles';
import { normalizeAngle, type OBB } from '../geometry';
import { selectionTarget } from '../groups';
import { endAt } from './connector';
import { ANCHOR_DOT_HIT_PX, anchorDotHit, anchorPoint, bendFor, type Side } from '../connectors';
import { handleWorldPos } from '../handles';
import type { SceneObject } from '../spatial';
import { plusAt } from '../plus';
import { HIT_RADIUS_PX } from '../handles';
import { obbBounds } from '../geometry';
import { snapCandidates, snapMove, snapResize, snapToGrid } from '../snapping';
import { hitTest, marqueeSelect, SECTION_BAND_PX } from '../hit-test';
import { NO_HOVER, type Box, type EndInput, type Hover, type Intent, type KeyInput, type PointerInput, type SceneReader, type Tool } from './tool';
import type { Guide } from '../snapping';

const DRAG_THRESHOLD_PX = { mouse: 3, pen: 4, touch: 6 } as const;
const HIT_TOLERANCE_PX = { mouse: 4, pen: 8, touch: 10 } as const;
const DEG = 180 / Math.PI;

type State =
  | { s: 'idle' }
  | { s: 'cancelled' }
  | { s: 'pointing'; startScreen: Point; ids: string[]; clicked: string | null; wasSelected: boolean }
  | { s: 'moving'; startScreen: Point; ids: string[]; snap: Snap | null; copy: boolean }
  | { s: 'resizing'; id: string; handle: HandleId; start: OBB; snap: Snap | null; aspect: number | null }
  | { s: 'rotating'; ids: string[]; pivot: Point; startAngle: number; startRotation: number }
  | { s: 'marquee'; startWorld: Point; add: boolean }
  | { s: 'reconnecting'; id: string; which: 'start' | 'end' }
  | { s: 'connecting'; from: EndInput; startScreen: Point }
  | { s: 'bending'; id: string; a: Point; b: Point; startScreen: Point }
  | { s: 'plus'; id: string; dir: Side; startScreen: Point }
  | { s: 'lockedPress'; startScreen: Point; id: string }
  | { s: 'panning'; last: Point }; // a finger on empty space (mobile): the view follows it

/** What a gesture snaps against, captured once at its start. */
type Snap = { start: Rect; neighbors: Rect[] };
export type SnapSettings = { enabled: boolean; grid: number | null };

const SIDES = new Set<HandleId>(['n', 'e', 's', 'w']);
/** Within the middle half of the box on both axes. */
function deepInside(o: OBB, p: Point): boolean {
  const { x, y } = toLocal(o, p);
  return Math.abs(x) <= o.w / 4 && Math.abs(y) <= o.h / 4;
}

/** `aspect`: the ratio a corner drag must give (images), whatever the box's current one; the opposite corner stays put. */
function resizeBox(o: OBB, handle: HandleId, pWorld: Point, keepRatio: boolean, aspect: number | null = null): Box {
  const hw = o.w / 2;
  const hh = o.h / 2;
  const movesX = handle.includes('e') || handle.includes('w');
  const movesY = handle.includes('n') || handle.includes('s');
  const ax = handle.includes('e') ? -hw : handle.includes('w') ? hw : 0;
  const ay = handle.includes('s') ? -hh : handle.includes('n') ? hh : 0;
  let { x: px, y: py } = toLocal(o, pWorld);
  if (aspect !== null && movesX !== movesY) {
    // A side of an image: the opposite side stays, the other dimension follows the ratio, centred on its axis.
    const [bw, bh] = [o.w, o.w / aspect];
    const scale = Math.max(1 / bw, 1 / bh, movesX ? Math.abs(px - ax) / bw : Math.abs(py - ay) / bh);
    const [w, h] = [bw * scale, bh * scale];
    const c = movesX ? toWorld(o, { x: ax + (px - ax >= 0 ? 1 : -1) * (w / 2), y: 0 }) : toWorld(o, { x: 0, y: ay + (py - ay >= 0 ? 1 : -1) * (h / 2) });
    return { x: c.x - w / 2, y: c.y - h / 2, w, h };
  }
  if (aspect !== null && movesX && movesY) {
    const [bw, bh] = [o.w, o.w / aspect];
    // The minimum size is a minimum scale, so a thin panorama keeps its ratio instead of clamping one side.
    const scale = Math.max(1 / bw, 1 / bh, Math.abs(px - ax) / bw, Math.abs(py - ay) / bh);
    const [w, h] = [bw * scale, bh * scale];
    const c = toWorld(o, { x: ax + (px - ax >= 0 ? 1 : -1) * (w / 2), y: ay + (py - ay >= 0 ? 1 : -1) * (h / 2) });
    return { x: c.x - w / 2, y: c.y - h / 2, w, h };
  }
  if (keepRatio && movesX && movesY) {
    const scale = Math.max(Math.abs(px - ax) / o.w, Math.abs(py - ay) / o.h);
    px = ax + (px - ax >= 0 ? 1 : -1) * o.w * scale;
    py = ay + (py - ay >= 0 ? 1 : -1) * o.h * scale;
  }
  const [x0, x1] = movesX ? [Math.min(ax, px), Math.max(ax, px)] : [-hw, hw];
  const [y0, y1] = movesY ? [Math.min(ay, py), Math.max(ay, py)] : [-hh, hh];
  const w = Math.max(1, x1 - x0); // dragging past the opposite edge flips the box (min/max), never a negative size
  const h = Math.max(1, y1 - y0);
  const c = toWorld(o, { x: (x0 + x1) / 2, y: (y0 + y1) / 2 });
  return { x: c.x - w / 2, y: c.y - h / 2, w, h };
}

/**
 * An image resized with the grid on (P16): the dragged edge (the horizontal one for a corner) goes to the grid and the
 * other side follows the ratio; the opposite edges stay. Unchanged when the grid would collapse the box.
 */
function snapAspectToGrid(box: Box, handle: HandleId, aspect: number, grid: number): Box {
  const xs = handle.includes('e') ? 'max' : handle.includes('w') ? 'min' : null;
  const ys = handle.includes('s') ? 'max' : handle.includes('n') ? 'min' : null;
  let [w, h] = [box.w, box.h];
  if (xs) {
    const t = snapToGrid(xs === 'max' ? box.x + box.w : box.x, grid);
    w = xs === 'max' ? t - box.x : box.x + box.w - t;
    h = w / aspect;
  } else if (ys) {
    const t = snapToGrid(ys === 'max' ? box.y + box.h : box.y, grid);
    h = ys === 'max' ? t - box.y : box.y + box.h - t;
    w = h * aspect;
  }
  if (!(w >= 1 && h >= 1)) return box;
  const x = xs === 'min' ? box.x + box.w - w : xs === 'max' ? box.x : box.x + (box.w - w) / 2;
  const y = ys === 'min' ? box.y + box.h - h : ys === 'max' ? box.y : box.y + (box.h - h) / 2;
  return { x, y, w, h };
}

export function createSelectTool(
  reader: SceneReader,
  camera: () => Camera,
  opts: { snap?: () => SnapSettings; viewport?: () => Rect; canConnect?: () => boolean } = {},
): Tool {
  let state: State = { s: 'idle' };
  let hovered: Hover = NO_HOVER;
  /** Snapping context for the given moving ids (null when snapping is off): their bounds and the visible neighbors. */
  const snapFor = (ids: readonly string[]): Snap | null => {
    if (!opts.snap?.().enabled || !opts.viewport) return null;
    const objs = reader.objects();
    const moving = new Set(ids);
    // Never snap to what moves with the selection (descendants) nor to what contains it (ancestors).
    const exclude = new Set<string>(ids);
    const chain = (id: string) => {
      const out: string[] = [];
      for (let p = objs.get(id)?.parentId ?? null; p !== null && !out.includes(p); p = objs.get(p)?.parentId ?? null) out.push(p);
      return out;
    };
    for (const id of ids) chain(id).forEach((a) => exclude.add(a));
    for (const o of objs.values()) if (chain(o.id).some((a) => moving.has(a))) exclude.add(o.id);
    const rects = ids.map((id) => objs.get(id)).filter((o): o is NonNullable<typeof o> => !!o).map((o) => obbBounds(o.obb));
    if (rects.length === 0) return null;
    const start = {
      minX: Math.min(...rects.map((r) => r.minX)),
      minY: Math.min(...rects.map((r) => r.minY)),
      maxX: Math.max(...rects.map((r) => r.maxX)),
      maxY: Math.max(...rects.map((r) => r.maxY)),
    };
    return { start, neighbors: snapCandidates(reader.index(), objs, opts.viewport(), exclude) };
  };
  const grid = () => opts.snap?.().grid ?? null;
  const moveIntents = (st: Extract<State, { s: 'moving' }>, e: PointerInput, commit: boolean): Intent[] => {
    const z = zoom();
    let dx = (e.x - st.startScreen.x) / z;
    let dy = (e.y - st.startScreen.y) / z;
    const copy = st.copy ? { copy: true } : {};
    if (!st.snap || e.meta) return [{ kind: 'translate', ids: st.ids, dx, dy, commit, ...copy }, { kind: 'guides', guides: [] }];
    const r = st.snap.start;
    const out = snapMove({ minX: r.minX + dx, minY: r.minY + dy, maxX: r.maxX + dx, maxY: r.maxY + dy }, st.snap.neighbors, z, { grid: grid() });
    dx += out.dx;
    dy += out.dy;
    return [{ kind: 'translate', ids: st.ids, dx, dy, commit, ...copy }, { kind: 'guides', guides: commit ? [] : out.guides }];
  };
  const resizeIntents = (st: Extract<State, { s: 'resizing' }>, e: PointerInput, commit: boolean): Intent[] => {
    let box = resizeBox(st.start, st.handle, world(e), e.shift, st.aspect);
    let guides: Guide[] = [];
    // Unrotated boxes only. A ratio lock (images) never snaps to neighbors (it would fight them), only to the grid.
    const g = grid();
    if (st.snap && st.aspect !== null && g && !e.meta && st.start.rotation === 0) box = snapAspectToGrid(box, st.handle, st.aspect, g);
    if (st.snap && st.aspect === null && !e.meta && !e.shift && st.start.rotation === 0) {
      const edges = {
        x: st.handle.includes('w') ? ('min' as const) : st.handle.includes('e') ? ('max' as const) : null,
        y: st.handle.includes('n') ? ('min' as const) : st.handle.includes('s') ? ('max' as const) : null,
      };
      const out = snapResize({ minX: box.x, minY: box.y, maxX: box.x + box.w, maxY: box.y + box.h }, edges, st.snap.neighbors, zoom(), { grid: g });
      box = { x: out.box.minX, y: out.box.minY, w: Math.max(1, out.box.maxX - out.box.minX), h: Math.max(1, out.box.maxY - out.box.minY) };
      guides = out.guides;
    }
    return [{ kind: 'resize', id: st.id, box, commit }, { kind: 'guides', guides: commit ? [] : guides }];
  };
  const world = (e: { x: number; y: number }) => screenToWorld(camera(), e);
  const zoom = () => camera().z;
  const target = (id: string) => selectionTarget(reader.objects(), id, reader.entered?.() ?? null);

  /** Objects that show anchor dots: never connectors nor groups. */
  const connectable = (o: SceneObject | undefined): o is SceneObject => !!o && o.type !== 'connector' && o.type !== 'group';
  /**
   * The anchor dot under `p`: of the hovered object, else of the single selected one (touch and pen have no hover).
   * Its world distance lets a closer resize or rotate handle win.
   */
  const dotHit = (p: Point, pointerType: PointerInput['pointerType']): { end: EndInput; side: Side; id: string; dist: number } | null => {
    if (opts.canConnect && !opts.canConnect()) return null;
    const sel = reader.selection();
    const ids = [hovered.id, sel.length === 1 ? sel[0]! : null].filter((id): id is string => !!id);
    for (const id of new Set(ids)) {
      const o = reader.objects().get(id);
      if (!connectable(o)) continue;
      const hit = anchorDotHit(o.obb, p, zoom(), Math.max(ANCHOR_DOT_HIT_PX, HIT_RADIUS_PX[pointerType]));
      if (!hit) continue;
      const anchor = { nx: hit.side === 'right' ? 1 : hit.side === 'left' ? 0 : 0.5, ny: hit.side === 'bottom' ? 1 : hit.side === 'top' ? 0 : 0.5 };
      const at = anchorPoint(o.obb, anchor);
      return { end: { objectId: o.id, anchor, x: at.x, y: at.y }, side: hit.side, id: o.id, dist: hit.dist };
    }
    return null;
  };
  /** The plus button under `p` of the single selected sticky note (unlocked, connecting allowed). */
  const plusHit = (p: Point): { id: string; side: Side } | null => {
    const sel = reader.selection();
    if (sel.length !== 1 || (opts.canConnect && !opts.canConnect())) return null;
    const o = reader.objects().get(sel[0]!);
    if (o?.type !== 'sticky' || o.locked) return null;
    const side = plusAt(o.obb, p, zoom());
    return side ? { id: o.id, side } : null;
  };
  /** A handle of the single selected object under `p`, unless an anchor dot is closer. */
  const handleHit = (p: Point, pointerType: PointerInput['pointerType'], dot: { dist: number } | null): { o: SceneObject; handle: HandleId } | null => {
    const sel = reader.selection();
    if (sel.length !== 1) return null;
    const found = reader.objects().get(sel[0]!);
    // Locked objects, groups and connectors (their ends are their handles) have no box handles; containers never rotate.
    const o = found && !found.locked && found.type !== 'group' && found.type !== 'connector' ? found : undefined;
    if (!o) return null;
    const near = handleAt(o.obb, p, zoom(), pointerType, handlesFor(o.type));
    // A side handle never claims the middle half of the box (a small object at low zoom, or under a finger, still moves).
    const handle = near && SIDES.has(near) && deepInside(o.obb, p) ? null : near;
    if (handle) {
      const hp = handleWorldPos(o.obb, handle, zoom());
      return dot && dot.dist < Math.hypot(hp.x - p.x, hp.y - p.y) ? null : { o, handle };
    }
    // Then a whole side: the grip is the edge, not only its middle handle (an image's sides keep its ratio).
    const side = edgeAt(o.obb, p, zoom(), pointerType);
    return side && !(dot && dot.dist < side.dist) ? { o, handle: side.edge } : null;
  };

  return {
    onPointerDown(e) {
      const sel = reader.selection();
      const p = world(e);
      state = { s: 'idle' };
      const dot = dotHit(p, e.pointerType);
      const plus = plusHit(p);
      if (plus) {
        state = { s: 'plus', id: plus.id, dir: plus.side, startScreen: { x: e.x, y: e.y } };
        return [];
      }

      if (sel.length === 1) {
        // A selected connector's ends are its handles.
        const ends = reader.endpoints?.(sel[0]!);
        const conn = reader.objects().get(sel[0]!);
        if (ends && conn && !conn.locked) {
          const r = HIT_RADIUS_PX[e.pointerType] / zoom();
          const which = Math.hypot(p.x - ends.end.x, p.y - ends.end.y) <= r ? 'end' : Math.hypot(p.x - ends.start.x, p.y - ends.start.y) <= r ? 'start' : null;
          if (which) {
            state = { s: 'reconnecting', id: conn.id, which };
            return [];
          }
          const mid = reader.midpoint?.(conn.id);
          if (mid && Math.hypot(p.x - mid.x, p.y - mid.y) <= r) {
            state = { s: 'bending', id: conn.id, a: ends.start, b: ends.end, startScreen: { x: e.x, y: e.y } };
            return [];
          }
        }
        const { o, handle } = handleHit(p, e.pointerType, dot) ?? {};
        if (o && handle === 'rotate') {
          const pivot = { x: o.obb.cx, y: o.obb.cy };
          state = { s: 'rotating', ids: [o.id], pivot, startAngle: Math.atan2(p.y - pivot.y, p.x - pivot.x) * DEG + 90, startRotation: o.obb.rotation };
          return [];
        }
        if (o && handle) {
          state = { s: 'resizing', id: o.id, handle, start: o.obb, snap: snapFor([o.id]), aspect: reader.aspect?.(o.id) ?? null };
          return [];
        }
      }

      if (dot) {
        state = { s: 'connecting', from: dot.end, startScreen: { x: e.x, y: e.y } };
        return [];
      }

      // The DOM saw a section's title or grip under the pointer (it sits outside the box, P5e).
      const picked = e.pick ? reader.objects().get(e.pick) : undefined;
      const band = SECTION_BAND_PX[e.pointerType];
      const deepest = picked ? (picked.locked ? null : picked.id) : hitTest(reader.index(), reader.objects(), p, HIT_TOLERANCE_PX[e.pointerType], zoom(), false, band);
      const hit = deepest && target(deepest);
      if (hit) {
        if (e.shift) return [{ kind: 'select', ids: [hit], mode: 'toggle' }];
        const wasSelected = sel.includes(hit);
        state = { s: 'pointing', startScreen: { x: e.x, y: e.y }, ids: wasSelected ? [...sel] : [hit], clicked: hit, wasSelected };
        return wasSelected ? [] : [{ kind: 'select', ids: [hit], mode: 'replace' }];
      }
      // A locked object can still be selected (so it can be unlocked), never dragged.
      const lockedHit = picked ? picked.id : hitTest(reader.index(), reader.objects(), p, HIT_TOLERANCE_PX[e.pointerType], zoom(), true, band);
      const locked = lockedHit && target(lockedHit);
      if (locked) {
        state = { s: 'lockedPress', startScreen: { x: e.x, y: e.y }, id: locked };
        return [{ kind: 'select', ids: [locked], mode: e.shift ? 'toggle' : 'replace' }];
      }
      // A finger on empty space drags the view (a marquee under a thumb is a slip, not an intent); multi-select mode keeps it.
      if (e.pointerType === 'touch' && !e.shift) {
        state = { s: 'panning', last: { x: e.x, y: e.y } };
        return [{ kind: 'select', ids: [], mode: 'replace' }];
      }
      state = { s: 'marquee', startWorld: p, add: e.shift };
      return e.shift ? [] : [{ kind: 'select', ids: [], mode: 'replace' }];
    },

    onPointerMove(e) {
      const st = state;
      const z = zoom();
      if (st.s === 'pointing') {
        if (Math.hypot(e.x - st.startScreen.x, e.y - st.startScreen.y) < DRAG_THRESHOLD_PX[e.pointerType]) return [];
        state = { s: 'moving', startScreen: st.startScreen, ids: st.ids, snap: snapFor(st.ids), copy: e.alt };
        return this.onPointerMove(e);
      }
      if (st.s === 'lockedPress') {
        if (Math.hypot(e.x - st.startScreen.x, e.y - st.startScreen.y) < DRAG_THRESHOLD_PX[e.pointerType]) return [];
        state = { s: 'cancelled' };
        return [{ kind: 'notice', notice: 'locked', id: st.id }];
      }
      if (st.s === 'panning') {
        const dx = e.x - st.last.x;
        const dy = e.y - st.last.y;
        state = { s: 'panning', last: { x: e.x, y: e.y } };
        return dx || dy ? [{ kind: 'pan', dx, dy }] : [];
      }
      if (st.s === 'moving') return moveIntents(st, e, false);
      if (st.s === 'resizing') return resizeIntents(st, e, false);
      if (st.s === 'rotating') return [rotateIntent(st, world(e), e.shift, false)];
      if (st.s === 'reconnecting') return [{ kind: 'reconnect', id: st.id, which: st.which, to: endAt(reader, world(e), z, new Set([st.id])), commit: false }];
      if (st.s === 'connecting') {
        if (Math.hypot(e.x - st.startScreen.x, e.y - st.startScreen.y) < DRAG_THRESHOLD_PX[e.pointerType]) return [];
        return [{ kind: 'connect', from: st.from, to: endAt(reader, world(e), z, new Set([st.from.objectId!])), commit: false }];
      }
      if (st.s === 'bending') {
        if (Math.hypot(e.x - st.startScreen.x, e.y - st.startScreen.y) < DRAG_THRESHOLD_PX[e.pointerType]) return [];
        return [{ kind: 'bend', id: st.id, bend: bendFor(st.a, st.b, world(e)), commit: false }];
      }
      if (st.s === 'marquee') {
        const p = world(e);
        const rect: Rect = {
          minX: Math.min(st.startWorld.x, p.x), minY: Math.min(st.startWorld.y, p.y),
          maxX: Math.max(st.startWorld.x, p.x), maxY: Math.max(st.startWorld.y, p.y),
        };
        const ids = [...new Set(marqueeSelect(reader.index(), reader.objects(), rect).map(target))];
        return [{ kind: 'marquee', rect }, { kind: 'select', ids, mode: st.add ? 'add' : 'replace' }];
      }
      return [];
    },

    onPointerUp(e) {
      const st = state;
      state = { s: 'idle' };
      const z = zoom();
      if (st.s === 'pointing') {
        // A click on an already-selected object inside a multi-selection narrows the selection to it.
        return st.wasSelected && st.ids.length > 1 && !e.shift && st.clicked ? [{ kind: 'select', ids: [st.clicked], mode: 'replace' }] : [];
      }
      if (st.s === 'moving') return moveIntents(st, e, true);
      if (st.s === 'resizing') return resizeIntents(st, e, true);
      if (st.s === 'rotating') return [rotateIntent(st, world(e), e.shift, true)];
      if (st.s === 'reconnecting') return [{ kind: 'reconnect', id: st.id, which: st.which, to: endAt(reader, world(e), z, new Set([st.id])), commit: true }];
      if (st.s === 'connecting') {
        if (Math.hypot(e.x - st.startScreen.x, e.y - st.startScreen.y) < DRAG_THRESHOLD_PX[e.pointerType]) return [];
        const to = endAt(reader, world(e), z, new Set([st.from.objectId!]));
        return to.objectId ? [{ kind: 'connect', from: st.from, to, commit: true }] : [{ kind: 'connectOpen', from: st.from, at: { x: to.x, y: to.y } }];
      }
      if (st.s === 'bending') {
        if (Math.hypot(e.x - st.startScreen.x, e.y - st.startScreen.y) < DRAG_THRESHOLD_PX[e.pointerType]) return [];
        return [{ kind: 'bend', id: st.id, bend: bendFor(st.a, st.b, world(e)), commit: true }];
      }
      if (st.s === 'plus') {
        // A click: a drag that started on the button does nothing.
        return Math.hypot(e.x - st.startScreen.x, e.y - st.startScreen.y) < DRAG_THRESHOLD_PX[e.pointerType] ? [{ kind: 'neighbor', id: st.id, dir: st.dir }] : [];
      }
      if (st.s === 'marquee') return [{ kind: 'marquee', rect: null }];
      return [];
    },

    onKey(e: KeyInput) {
      if (e.key === 'Escape') {
        if (state.s === 'idle' || state.s === 'cancelled') return [{ kind: 'select', ids: [], mode: 'replace' }];
        const wasMarquee = state.s === 'marquee';
        state = { s: 'cancelled' };
        return wasMarquee ? [{ kind: 'marquee', rect: null }, { kind: 'cancel' }] : [{ kind: 'cancel' }];
      }
      const sel = [...reader.selection()];
      if (sel.length === 0) return [];
      const step = e.shift ? 10 : 1;
      const nudge: Record<string, [number, number]> = { ArrowLeft: [-step, 0], ArrowRight: [step, 0], ArrowUp: [0, -step], ArrowDown: [0, step] };
      const d = nudge[e.key];
      if (d) return [{ kind: 'translate', ids: sel, dx: d[0], dy: d[1], commit: true }];
      if (e.key === 'Delete' || e.key === 'Backspace') return [{ kind: 'command', id: 'delete' }];
      return [];
    },

    onHover(e) {
      if (state.s !== 'idle' && state.s !== 'cancelled') return [];
      hovered = hoverAt(e);
      return [{ kind: 'hover', hover: hovered }];
    },

    onCancel() {
      const wasActive = state.s !== 'idle' && state.s !== 'cancelled';
      state = { s: 'cancelled' };
      return wasActive ? [{ kind: 'cancel' }] : [];
    },
  };

  /** What the pointer rests on: a handle of the single selected object first, then the object under it. */
  function hoverAt(e: PointerInput): Hover {
    const p = world(e);
    const plus = plusHit(p);
    if (plus) return { ...NO_HOVER, id: plus.id, plus: plus.side };
    const dot = dotHit(p, e.pointerType);
    const h = handleHit(p, e.pointerType, dot);
    if (h) return { ...NO_HOVER, id: h.o.id, handle: h.handle };
    if (dot) return { ...NO_HOVER, id: dot.id, dot: dot.side };
    // A locked section is outlined but never offered as grabbable (no move cursor for a move that cannot happen).
    const picked = e.pick ? reader.objects().get(e.pick) : undefined;
    if (picked) return { ...NO_HOVER, id: target(picked.id), ...(picked.locked ? {} : { grab: true }) };
    const deepest = hitTest(reader.index(), reader.objects(), p, HIT_TOLERANCE_PX[e.pointerType], zoom(), true, SECTION_BAND_PX[e.pointerType]);
    const o = deepest ? reader.objects().get(deepest) : undefined;
    return { ...NO_HOVER, id: deepest ? target(deepest) : null, ...(o?.type === 'section' && !o.locked ? { grab: true } : {}) };
  }

  function rotateIntent(st: Extract<State, { s: 'rotating' }>, p: Point, snap: boolean, commit: boolean): Intent {
    let delta = normalizeAngle(Math.atan2(p.y - st.pivot.y, p.x - st.pivot.x) * DEG + 90 - st.startAngle);
    if (snap) delta = Math.round((st.startRotation + delta) / 15) * 15 - st.startRotation; // the object's own angle lands on 15°
    return { kind: 'rotate', ids: st.ids, deltaDeg: delta, pivot: st.pivot, commit };
  }
}
